import React, { useEffect, useMemo, useCallback, useState } from 'react';
import styles from './Data.module.css';
import cx from 'classnames';
import { Type } from '../../../types/Type';
import { useOnboardingContext } from '../../../contexts/OnboardingContext';
import { api } from '../../../services/ApiService';
import Input from '../../../components/Input';

interface DataStepProps {
    isActive?: boolean;
}

const CUSTOM_TYPE_NAME = 'Andere branche';

const DataStep: React.FC<DataStepProps> = ({ isActive = false }) => {
    const { formData, toggleType, types, typesLoading, loadTypes, setUserDataField } = useOnboardingContext();
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [previewType, setPreviewType] = useState<Type | null>(null);

    useEffect(() => {
        loadTypes();
    }, [loadTypes]);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            setMousePos({ x: e.clientX, y: e.clientY });
        };
        window.addEventListener('mousemove', onMouseMove);
        return () => window.removeEventListener('mousemove', onMouseMove);
    }, []);

    const handleTypeSelect = useCallback((type: Type) => {
        toggleType(type);
    }, [toggleType]);

    const isTypeSelected = useCallback((typeId: number) =>
        formData.selectedTypes.some(t => t.id === typeId),
        [formData.selectedTypes]
    );

    const isCustomTypeSelected = useMemo(() =>
        formData.selectedTypes.some(t => t.name === CUSTOM_TYPE_NAME),
        [formData.selectedTypes]
    );

    const getImageUrl = useCallback((picture: string): string => {
        const apiBaseUrl = api.getBaseUrl();
        return `${apiBaseUrl}storage/pictures/${picture}`;
    }, []);

    const arrowSvg = (
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
            <path d="M7 17L17 7M7 7h10v10" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    return (
        <div className={cx(styles['data-step-container'])} hidden={!isActive}>
            <div className={styles['sort-head']}>
                <h2 className={styles['sort-head-title']}>Branche</h2>
                <span className={styles['sort-head-sub']}>
                    {types.length} Kategorien
                </span>
            </div>
            <div className={styles['types-container']}>
                {typesLoading ? (
                    <p className={styles['loading-text']}>Branchen werden geladen...</p>
                ) : types.length === 0 ? (
                    <p className={styles['loading-text']}>Keine Branchen verfügbar.</p>
                ) : (
                    <div className={styles['sort-list']}>
                        {types.map((type, index) => {
                            const imgUrl = type.name !== CUSTOM_TYPE_NAME ? getImageUrl(type.picture) : '';
                            return (
                                <div
                                    key={type.id}
                                    className={cx(
                                        styles['sort-item'],
                                        isTypeSelected(type.id) && styles['selected']
                                    )}
                                    data-img={imgUrl || undefined}
                                    onClick={() => handleTypeSelect(type)}
                                    onMouseEnter={() => imgUrl && setPreviewType(type)}
                                    onMouseLeave={() => setPreviewType(null)}
                                >
                                    <span className={styles['sort-num']}>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    <span className={styles['sort-name']}>{type.name}</span>
                                    <span className={styles['sort-arrow']}>{arrowSvg}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Cursor-following preview image */}
            <div
                className={cx(styles['sort-preview'], previewType && styles['show'])}
                style={{
                    left: mousePos.x + 24,
                    top: mousePos.y - 140,
                }}
            >
                {previewType && (
                    <img
                        src={getImageUrl(previewType.picture)}
                        alt={previewType.name}
                    />
                )}
            </div>

            {isCustomTypeSelected && (
                <div className={styles['custom-type-container']}>
                    <label className={styles['custom-type-label']}>
                        Andere branche
                    </label>
                    <Input
                        type="text"
                        placeholder="Bitte Branche angeben:"
                        property="customTypeName"
                        value={formData.userData.customTypeName}
                        onChange={(property, value) => setUserDataField(property, value)}
                        classes={styles['custom-type-input']}
                    />
                </div>
            )}
        </div>
    );
};

export default DataStep;
